import {get, put} from '@/assets/plugins/mps-common/app/local-storage-helper'
import {isNOU} from "../expansions/condition";
import {dateHelper} from "@/assets/plugins/mps-common/time/date-helper";
import {PATH} from "@/constants";
import VueAccessor from "../app/VueAccessor";
import {permissionCanonicalNameManager} from "@/assets/plugins/mps-common/user/permission-canonical-name-manager";
import {translationManager} from "@/assets/plugins/mps-common/translation/translation-manager";
import {nativeBridge} from "@/assets/plugins/mps-mobile-native-bridge/native-bridge";
import {pref} from "@/assets/plugins/mps-common/preferences/preferences";
import Permissions from "@/constants/permissions";
import {SORT_USER} from "@/constants/etc-code";
import {capacitorPlugin} from "@/assets/plugins/mps-mobile-capacitor-app/capacitor-plugin"

const KEY_ACCESS_TOKEN = 'key_access_token'
const KEY_REFRESH_TOKEN = 'key_refresh_token'
const KEY_USER = 'key_user';

/**
 * MPS USER 를 위한 관리자 입니다.
 * 로그인 정보, 유저 정보, 및 API 접근 토큰 관리자입니다.
 * 어플리케이션 내에서 한개만 유지되어야 합니다.
 */
class UserManager extends VueAccessor {

  _accessToken;
  _refreshToken
  _firebaseToken;
  // JWT 토큰 재발급 요청 상태
  // (어떤 request가 이미 재발급 요청을 보낸 상태)
  _isTokenRefreshing
  _isTokenSending = false
  _isLoginApproved
  _user = {
    rentalcompanyCountryCode: undefined,
    rentalcompanyLanguageCode: undefined,
    countryCode: undefined,
    languageCode: undefined,
    name: undefined,
    canonicalNameList: [],
    rentalcompanyId: undefined,
    rentalcompanyName: undefined,
    roles: [],
    userId: undefined,
    isAdmin: undefined,
    isWorker: undefined,
  };
  _permissionCanonicalNameManager;
  _nativeBridge;
  _sort;
  _beLogout = false;

  constructor() {
    super();
    this._permissionCanonicalNameManager = permissionCanonicalNameManager;
    this._restorePersistenceData();
    if (!!this.user) nativeBridge.putUser(this.user.userId, this.accessToken);
  }

  /**
   * 영속 데이터를 복구합니다.
   * @private
   */
  _restorePersistenceData() {
    this.accessToken = pref.get(KEY_ACCESS_TOKEN, null)
    this.refreshToken = pref.get(KEY_REFRESH_TOKEN, null)
    this.isLoginApproved = true
    this.user = get(KEY_USER, null);
    this.firebaseToken = pref.get('fcm_token', '');
    
    if (!!this.user) {
      if (!!this.user.canonicalNameList) {
        this._permissionCanonicalNameManager.setPermissions(this.user.canonicalNameList);
        if (this.hasPermission(Permissions.RENTAL_ADMIN)) {
          this._user.isAdmin = true;
        } else if (this.hasPermission(Permissions.RENTAL_WORKER)) {
          this._user.isWorker = true;
        }
      }
      this._sort = !!this._user.rentalcompanyId ? SORT_USER.RENTALCOMPANY : SORT_USER.COMMUNITY;
      nativeBridge.putUser(this.user.userId, this.accessToken);
    } else {
      this.sendTokenToServer();
    }
  }

  set nativeBridge(nativeBridge) {
    this._nativeBridge = nativeBridge;
  }

  set accessToken(accessToken) {
    this._accessToken = accessToken;
    pref.put(KEY_ACCESS_TOKEN, accessToken);
    capacitorPlugin.saveNativePreference('accessToken', this.accessToken);
  }

  get isCommunityUser() {
    return this._sort === SORT_USER.COMMUNITY;
  }

  get isRentalUser() {
    return this._sort === SORT_USER.RENTALCOMPANY;
  }

  get accessToken() {
    return this._accessToken;
  }

  set refreshToken(refreshToken) {
    this._refreshToken = refreshToken
    pref.put(KEY_REFRESH_TOKEN, refreshToken)
  }

  get refreshToken() {
    return this._refreshToken
  }

  set isTokenRefreshing(value) {
    this._isTokenRefreshing = value
  }

  get isTokenRefreshing() {
    return this._isTokenRefreshing
  }

  set isLoginApproved(value) {
    this._isLoginApproved = value
  }

  get isLoginApproved() {
    return this._isLoginApproved
  }

  get firebaseToken() {
    return this._firebaseToken;
  }

  set firebaseToken(value) {
    this._firebaseToken = value;
  }

  get locale() {
    return (this.user.languageCode || "").toLowerCase() + "-" + (this.user.countryCode || "").toUpperCase();
  }

  get isKorean() {
    return this.user.rentalcompanyCountryCode === "KR";
  }

  get isForeign() {
    return !this.isKorean;
  }

  set user(user) {
    this._user = user;
    put(KEY_USER, user);
    if (!!user) {
      dateHelper.setUser(user);
    }
  }

  get user() {
    return this._user;
  }

  get hasAccessToken() {
    return this.accessToken !== null;
  }

  get authorization() {
    return this.hasAccessToken ? 'Bearer ' + this.accessToken : null;
  }

  get validate() {
    return !(isNOU(this.accessToken) || isNOU(this.refreshToken) || isNOU(this.user));
  }

  hasPermission(canonicalPermissionName) {
    return this._permissionCanonicalNameManager.hasPermission(canonicalPermissionName);
  }

  async login(rentalCompanyId, loginId, password) {
    const request = this.vm.$request(PATH.USER_LOGIN)
      .setObject({
        rentalcompanyId: rentalCompanyId.trim(),
        loginId: loginId.trim(),
        password: password.trim(),
        mobileYn: 'Y'
      });
    return new Promise(async (resolve, reject) => {
      try {
        const response = await request.enqueue();
        this.isLoginApproved = true
        this.accessToken = response.accessToken
        this.refreshToken = response.refreshToken
        this.user = response.user;
        this._permissionCanonicalNameManager.setPermissions(this.user.canonicalNameList);
        if (this.hasPermission(Permissions.RENTAL_ADMIN)) {
          this._user.isAdmin = true;
        } else if (this.hasPermission(Permissions.RENTAL_WORKER)) {
          this._user.isWorker = true;
        }
        translationManager.save(response.languages);
        nativeBridge.putUser(this.user.userId, this.accessToken);
        this._sort = SORT_USER.RENTALCOMPANY;
        this.sendSavedPushTokenAfterLogin()
        
        resolve(response);
      } catch (e) {
        this._sort = null;
        console.log(e);
        reject(e);
      }
    });
  }

  async communityLogin(loginId, password) {
    const request = this.vm.$request(PATH.COMMUNITY_USER.LOGIN)
      .setObject({
        email: loginId.trim(),
        password: password.trim()
      })
      .catch();
    return new Promise(async (resolve, reject) => {
      try {
        const response = await request.enqueue();
        this.isLoginApproved = true
        this.accessToken = response.accessToken
        this.refreshToken = response.refreshToken
        this.vm.$set(this, 'user', {});
        this.user = response.communityUser;
        this._sort = SORT_USER.COMMUNITY;
        this.sendSavedPushTokenAfterLogin()
        
        resolve(response);
      } catch (e) {
        this._sort = null;
        console.error(e);
        reject(e);
      }
    });
  }

  async sendTokenToServer() {
    if (!this.user || !this.user.userId) return;
    try {
      const res = await this.vm.$request("user/fcm/reg")
        .setObject({
          token: this.firebaseToken,
          deviceType: 'MOBILE:APP',
          deviceId: this.deviceId,
          telNo: ''
        })
        .catch()
        .enqueue();
      console.log(res);
    } catch (e) {
      console.error(e);
      //do nothing.
    }
  }

  /**
   * capacitor App 에서 동작합니다.
   * @return {Promise<void>}
   */
  async sendTokenToServer2(token, deviceType, deviceId) {
    if (!this.user || !this.user.userId) {
      return
    }
    
    if (this._isTokenSending) {
      return
    }
    
    this._isTokenSending = true
    
    try {
      await this.vm.$request("user/fcm/reg")
        .setObject({
          token,
          deviceType,
          deviceId,
          telNo: ''
        })
        .catch()
        .enqueue()
    } catch (e) {
      console.error(e)
    } finally {
      this._isTokenSending = false
    }
  }

  /**
   * 로그인 후 저장된 푸시 토큰을 서버로 전송
   */
  async sendSavedPushTokenAfterLogin() {
    if (!capacitorPlugin.isCapacitorMobileApp) {
      return
    }
    
    const maxRetries = 3
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const { Preferences } = await import('@capacitor/preferences')
        const deviceInfo = await capacitorPlugin.getDeviceInfo()
        
        if (!deviceInfo.deviceId || !deviceInfo.deviceType) {
          if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 500))
            continue
          } else {
            return
          }
        }
        
        let token = null
        
        if (deviceInfo.deviceType === 'iOS') {
          if (window.CapacitorDeviceInfo?.apnsToken) {
            token = window.CapacitorDeviceInfo.apnsToken
          } else {
            const apnsResult = await Preferences.get({ key: 'apns_token' })
            if (apnsResult.value) {
              token = apnsResult.value
            }
          }
        }
        
        if (!token) {
          const fcmResult = await Preferences.get({ key: 'fcm_token' })
          if (fcmResult.value) {
            token = fcmResult.value
          }
        }
        
        if (token) {
          await this.sendTokenToServer2(token, deviceInfo.deviceType, deviceInfo.deviceId)
          return
        } else {
          if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 500))
            continue
          }
        }
      } catch (error) {
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 500))
        }
      }
    }
  }

  async logout() {
    return new Promise((resolve, reject) => {
      this.accessToken = null;
      this.refreshToken = null
      this.user = null;
      this.beLogout = true;
      this.vm.routerManager.push({name: 'LogoutDivider'});
      nativeBridge.putUser(null, null);
      capacitorPlugin.removeNativePreference('accessToken');
      resolve();
    });
  }

  /**
   * 로그아웃 처리가 되어서 라우터가 이동되었는지 여부를 나타내기 위한 플래그
   */
  get beLogout() {
    return this._beLogout;
  }

  /**
   * 로그아웃시 true 로 변경 router beforeEach에서 적절한 처리후 false로 변경한다.
   */
  set beLogout(beLogout) {
    this._beLogout = beLogout;
  }

  /**
   * 로그인 한 임대업체의 국가를 검사하여 노출 여부를 결정 합니다.
   * 유저의 국가는 상관 X
   * @param countryCode [String, Array]
   */
  countryFeature(countryCode) {
    if (!countryCode || countryCode === '') {
      alert('countryCode is null')
      return false
    }

    if (Array.isArray(countryCode) && countryCode.length > 0) {
      let result = false
      for (const e of countryCode) {
        if (!!e && e !== '' && this.checkCountryCode(e)) {
          result = true
          break
        }
      }
      return result
    } else if (typeof countryCode === 'string') {
      return this.checkCountryCode(countryCode)
    } else {
      return false
    }
  }

  checkCountryCode(countryCode) {
    const targetCountryCode = this._user.rentalcompanyCountryCode.toUpperCase()
    if (!targetCountryCode || targetCountryCode === '') {
      return false
    }
    return targetCountryCode === countryCode.toUpperCase()
  }
};

const userManager = new UserManager();

export default userManager;
